/**
 * 설정 페이지 - 프로필/비밀번호/세션/디스플레이/데이터/실험실
 */
import {
  getToken, removeToken,
  apiUpdateProfile, apiChangePassword, apiLogoutAll, apiExportData, apiDeleteData, apiDeleteAccount
} from '../lib/api';
import { getUserInfo } from '../lib/storage';
import { toast } from '../lib/toast';
import type { AppStateManager } from '../app';

let _app: AppStateManager;
const SP = 'kalpha_setting_';

function gs<T>(key: string, def: T): T {
  try { const v = localStorage.getItem(SP + key); return v ? JSON.parse(v) : def; } catch { return def; }
}
function ss<T>(key: string, val: T): void { localStorage.setItem(SP + key, JSON.stringify(val)); }

export function renderSettingsPage(container: HTMLElement, app: AppStateManager): void {
  _app = app;
  load();
}

async function load(): Promise<void> {
  const el = document.getElementById('settings-content')!;
  const token = getToken();
  const ui = getUserInfo();

  let sessionsHTML = ''; let sessionCount = 0;
  let ipInfoMap: Record<string, string> = {};
  try {
    const [sres, ipRes] = await Promise.all([
      fetch('/api/auth/sessions', { headers: { Authorization: `Bearer ${token}` } }),
      fetch('/api/proxy/ip')
    ]);
    const json = await sres.json();
    const ipJson = await ipRes.json().catch(() => ({}));
    const myLoc = [ipJson.city, ipJson.country].filter(Boolean).join(', ') || '';
    if (json.success && json.data) {
      sessionCount = json.data.length;
      ipInfoMap[json.data.find((s: any) => s.is_current)?.ip_address || ''] = myLoc;
      sessionsHTML = json.data.map((s: any) => {
        const ua = s.user_agent || '';
        const b = ua.includes('Edg')?'Edge':ua.includes('Chrome')?'Chrome':ua.includes('Firefox')?'Firefox':ua.includes('Safari')?'Safari':'Unknown';
        const o = ua.includes('Windows')?'Windows':ua.includes('Mac')?'Mac':ua.includes('Linux')?'Linux':ua.includes('Android')?'Android':ua.includes('iPhone')?'iPhone':'';
        const d = [o,b].filter(Boolean).join(' / ')||'알 수 없음';
        const loc = s.is_current && myLoc ? ` (${myLoc})` : '';
        return `<div class="flex items-center justify-between px-3 py-2.5 surface-secondary rounded-xl"><div class="min-w-0 flex-1"><div class="flex items-center gap-2"><div class="w-2.5 h-2.5 rounded-full shrink-0 ${s.is_current?'bg-kalpha-500':'bg-gray-300'}"></div><span class="text-sm text-primary truncate">${d}</span></div><div class="text-xs text-tertiary mt-0.5 ml-4">${(s.ip_address&&s.ip_address!=='알 수 없음')?s.ip_address+loc:''} / ${new Date(s.created_at*1000).toLocaleString()}${s.is_current?' <span class="text-xs text-green font-semibold">(현재 기기)</span>':''}</div></div></div>`;
      }).join('');
    }
  } catch {}

  const settings = {
    fontSize: gs('fontSize', 'medium'),
    bubbleColor: gs('bubbleColor', '#2d6a4f'),
    bubbleShape: gs('bubbleShape', 'round'),
    timeFormat: gs('timeFormat', 'relative'),
    joinNotify: gs('joinNotify', true),
    sidebarMode: gs('sidebarMode', false),
    animSpeed: gs('animSpeed', '1x'),
    compactMode: gs('compactMode', false),
    highContrast: gs('highContrast', false),
    classicTheme: gs('classicTheme', false),
    rainbowMode: gs('rainbowMode', false),
    markdownMode: gs('markdownMode', false),
    inputMaxLines: gs('inputMaxLines', 'unlimited'),
    pollInterval: gs('pollInterval', '3s'),
    showLatency: gs('showLatency', false),
    debugMode: gs('debugMode', false),
  };
  const visits = gs('visits', 0) + 1; ss('visits', visits);
  const msgCount = gs('msgCount', 0);

  const sel = (id: string, opts: Array<{v:string;l:string}>, cur: string) =>
    `<select id="${id}" class="input-field w-28 py-1.5 text-sm">${opts.map(o => `<option value="${o.v}" ${cur===o.v?'selected':''}>${o.l}</option>`).join('')}</select>`;

  const chk = (label: string, id: string, cur: boolean, desc?: string) => `
    <div class="flex justify-between items-center px-4 py-3">
      <div><span class="text-sm text-primary">${label}</span>${desc?`<p class="text-xs text-tertiary">${desc}</p>`:''}</div>
      <label class="relative inline-flex items-center cursor-pointer"><input id="${id}" type="checkbox" ${cur?'checked':''} class="sr-only peer"><div class="w-9 h-5 bg-gray-300 peer-focus:ring-2 peer-focus:ring-kalpha-400 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-kalpha-500"></div></label>
    </div>`;

  const opt = (label: string, id: string, opts: Array<{v:string;l:string}>, cur: string, desc?: string) => `
    <div class="flex justify-between items-center px-4 py-3">
      <div><span class="text-sm text-primary">${label}</span>${desc?`<p class="text-xs text-tertiary">${desc}</p>`:''}</div>
      ${sel(id, opts, cur)}
    </div>`;

  const color = (label: string, id: string, cur: string) => `
    <div class="flex justify-between items-center px-4 py-3">
      <span class="text-sm text-primary">${label}</span>
      <input id="${id}" type="color" value="${cur}" class="w-10 h-8 rounded cursor-pointer border-0 p-0">
    </div>`;

  el.innerHTML = `<div class="space-y-5">
    <div><div class="section-header mb-2">프로필</div><div class="surface-secondary rounded-xl p-4">
      <div class="flex gap-3"><input id="profile-name" type="text" class="input-field flex-1" value="${ui?.display_name||''}" placeholder="닉네임"><button id="btn-profile-save" class="btn btn-primary px-4 py-2.5 text-sm shrink-0">저장</button></div>
    </div></div>
    <div><div class="section-header mb-2">비밀번호 변경</div><div class="surface-secondary rounded-xl p-4 space-y-3">
      <form id="pw-form" onsubmit="return false">
      <input id="pw-current" type="password" class="input-field w-full" placeholder="현재 비밀번호" autocomplete="current-password">
      <input id="pw-new" type="password" class="input-field w-full" placeholder="새 비밀번호 (8자 이상)" autocomplete="new-password">
      <input id="pw-confirm" type="password" class="input-field w-full" placeholder="새 비밀번호 확인" autocomplete="new-password">
      </form>
      <button id="btn-pw-save" class="btn btn-primary w-full py-2.5 text-sm">비밀번호 변경</button>
    </div></div>
    <div><div class="section-header mb-2">로그인 세션 (${sessionCount})</div><div class="space-y-1 mb-2">${sessionsHTML||'<p class="empty-state text-sm text-center py-4">불러오기 실패</p>'}</div>${sessionCount>1?`<button id="btn-logout-all" class="btn btn-danger w-full py-2.5 text-sm">모든 기기 로그아웃</button>`:''}</div>
    <div><div class="section-header mb-2">디스플레이</div><div class="surface-secondary rounded-xl divide-y divide-[var(--border-light)]">
      ${opt('글꼴 크기', 'set-font-size', [{v:'small',l:'작게'},{v:'medium',l:'보통'},{v:'large',l:'크게'}], settings.fontSize)}
      ${color('내 말풍선 색상', 'set-bubble-color', settings.bubbleColor)}
      ${opt('말풍선 모양', 'set-bubble-shape', [{v:'round',l:'라운드'},{v:'square',l:'스퀘어'},{v:'tail',l:'꼬리'}], settings.bubbleShape)}
      ${opt('시간 표시', 'set-time-format', [{v:'relative',l:'상대시간'},{v:'24h',l:'24시간제'},{v:'12h',l:'12시간제'}], settings.timeFormat)}
      ${chk('방 입장 알림', 'set-join-notify', settings.joinNotify)}
      ${chk('PC 사이드바 모드', 'set-sidebar', settings.sidebarMode)}
    </div></div>
    <div><div class="section-header mb-2">데이터 관리</div><div class="surface-secondary rounded-xl divide-y divide-[var(--border-light)]">
      <button id="btn-export" class="w-full text-left px-4 py-3 text-sm text-primary hover:bg-surface-tertiary transition-colors flex justify-between"><span>데이터보내기 (JSON)</span><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-tertiary"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg></button>
      <button id="btn-delete-data" class="w-full text-left px-4 py-3 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors rounded-xl font-semibold">내 데이터 삭제 (방 + 메시지)</button>
      <button id="btn-delete-account" class="w-full text-left px-4 py-3 text-sm text-white bg-red-600 hover:bg-red-700 transition-colors rounded-xl font-semibold shadow-sm">회원 탈퇴</button>
    </div></div>
    <div><div class="section-header mb-2">실험실</div>
    <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 mb-3 text-xs text-amber-700 dark:text-amber-300">이 기능들은 사이트 사용 경험에 영향을 줄 수 있습니다. 일부 기능은 예상치 못한 동작을 일으킬 수 있습니다.</div>
    <div class="text-xs font-bold text-tertiary uppercase tracking-wider mb-2 px-1">디스플레이 실험</div>
    <div class="surface-secondary rounded-xl divide-y divide-[var(--border-light)] mb-3">
      ${opt('말풍선 애니메이션 속도', 'set-anim-speed', [{v:'0.5x',l:'0.5배'},{v:'1x',l:'1배'},{v:'2x',l:'2배'},{v:'off',l:'끄기'}], settings.animSpeed)}
      ${chk('컴팩트 모드', 'set-compact', settings.compactMode, '말풍선 간격을 좁힙니다')}
      ${chk('고대비 모드', 'set-contrast', settings.highContrast, '텍스트 가독성을 극대화합니다')}
      ${chk('클래식 테마', 'set-classic', settings.classicTheme, 'v1.0.0 시절 디자인')}
      ${chk('무지개 모드', 'set-rainbow', settings.rainbowMode, '메시지마다 말풍선 색상이 변경됩니다')}
    </div>
    <div class="text-xs font-bold text-tertiary uppercase tracking-wider mb-2 px-1">입력 실험</div>
    <div class="surface-secondary rounded-xl divide-y divide-[var(--border-light)] mb-3">
      ${chk('마크다운 입력', 'set-markdown', settings.markdownMode, '**굵게** *기울임* ~~취소선~~')}
      ${opt('입력창 높이 제한', 'set-input-lines', [{v:'3',l:'최대 3줄'},{v:'5',l:'최대 5줄'},{v:'unlimited',l:'무제한'}], settings.inputMaxLines)}
    </div>
    <div class="text-xs font-bold text-tertiary uppercase tracking-wider mb-2 px-1">네트워크 실험</div>
    <div class="surface-secondary rounded-xl divide-y divide-[var(--border-light)] mb-3">
      ${opt('폴링 간격', 'set-poll-interval', [{v:'1s',l:'1초'},{v:'3s',l:'3초'},{v:'5s',l:'5초'},{v:'10s',l:'10초'}], settings.pollInterval)}
    </div>
    <div class="text-xs font-bold text-tertiary uppercase tracking-wider mb-2 px-1">데이터</div>
    <div class="surface-secondary rounded-xl divide-y divide-[var(--border-light)] mb-3">
      <div class="px-4 py-3 text-sm text-primary">사용 통계: <span class="text-tertiary">방문 ${visits}회, 메시지 ${msgCount}개</span></div>
      <button id="btn-force-reset" class="w-full text-left px-4 py-3 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors rounded-xl font-semibold border border-amber-200 dark:border-amber-800">강제 초기화 (로컬 캐시 정리)</button>
    </div></div>
    <div><div class="section-header mb-2">키보드 단축키</div><div class="surface-secondary rounded-xl divide-y divide-[var(--border-light)]">
      <div class="flex justify-between px-4 py-3"><span class="text-sm text-primary">방 검색</span><kbd class="badge-shortcut">Ctrl + /</kbd></div>
      <div class="flex justify-between px-4 py-3"><span class="text-sm text-primary">새 채팅</span><kbd class="badge-shortcut">Ctrl + Shift + N</kbd></div>
      <div class="flex justify-between px-4 py-3"><span class="text-sm text-primary">모달 닫기</span><kbd class="badge-shortcut">Esc</kbd></div>
      <div class="flex justify-between px-4 py-3"><span class="text-sm text-primary">뒤로가기</span><kbd class="badge-shortcut">Alt + Left</kbd></div>
    </div></div>
  </div>`;

  bindProfile();
  bindPassword();
  bindSessions();
  bindData();
  bindLabs();
  applyAllSettings();
}

function bindProfile() {
  const btn = document.getElementById('btn-profile-save') as HTMLButtonElement;
  btn?.addEventListener('click', async () => {
    const n = (document.getElementById('profile-name') as HTMLInputElement).value.trim();
    if (!n) { toast('닉네임을 입력하세요', 'error'); return; }
    btn.disabled = true; btn.textContent = '저장 중...';
    try {
      const json = await apiUpdateProfile(n);
      if (json.success) { const ui = getUserInfo(); if (ui) { ui.display_name = n; localStorage.setItem('kalpha_user', JSON.stringify(ui)); } toast('저장됨', 'success'); }
      else toast(json.error || '실패', 'error');
    } finally { btn.disabled = false; btn.textContent = '저장'; }
  });
}

function bindPassword() {
  const btn = document.getElementById('btn-pw-save') as HTMLButtonElement;
  const form = document.getElementById('pw-form') as HTMLFormElement;

  async function doPasswordChange() {
    const cur = (document.getElementById('pw-current') as HTMLInputElement).value;
    const nw = (document.getElementById('pw-new') as HTMLInputElement).value;
    const cf = (document.getElementById('pw-confirm') as HTMLInputElement).value;
    if (!cur || !nw) { toast('입력해주세요', 'error'); return; }
    if (nw.length < 8) { toast('8자 이상', 'error'); return; }
    if (nw !== cf) { toast('비밀번호 불일치', 'error'); return; }
    btn.disabled = true; btn.textContent = '변경 중...';
    try {
      const json = await apiChangePassword(cur, nw);
      if (json.success) { toast('변경됨', 'success'); ['pw-current','pw-new','pw-confirm'].forEach(id => (document.getElementById(id) as HTMLInputElement).value = ''); }
      else toast(json.error || '실패', 'error');
    } finally { btn.disabled = false; btn.textContent = '비밀번호 변경'; }
  }

  btn?.addEventListener('click', doPasswordChange);
  form?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); doPasswordChange(); } });
}

function bindSessions() {
  const btn = document.getElementById('btn-logout-all') as HTMLButtonElement;
  btn?.addEventListener('click', async () => {
    if (!confirm('모든 기기에서 로그아웃됩니다. 계속하시겠습니까?')) return;
    btn.disabled = true; btn.textContent = '처리 중...';
    try {
      const json = await apiLogoutAll();
      if (json.success) { localStorage.removeItem('kalpha_token'); localStorage.removeItem('kalpha_user'); _app.navigateTo('login'); }
      else { toast('실패', 'error'); btn.disabled = false; btn.textContent = '모든 기기 로그아웃'; }
    } catch { btn.disabled = false; btn.textContent = '모든 기기 로그아웃'; }
  });
}

function bindData() {
  const exportBtn = document.getElementById('btn-export') as HTMLButtonElement;
  exportBtn?.addEventListener('click', async () => {
    exportBtn.disabled = true;
    try {
      const json = await apiExportData();
      if (json.success) { const blob = new Blob([JSON.stringify(json.data, null, 2)], { type: 'application/json' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'kalphaotalk-export.json'; a.click(); toast('보내기 완료', 'success'); }
      else toast('실패', 'error');
    } finally { exportBtn.disabled = false; }
  });

  const delDataBtn = document.getElementById('btn-delete-data') as HTMLButtonElement;
  delDataBtn?.addEventListener('click', async () => {
    if (!confirm('내가 만든 방과 메시지가 모두 삭제됩니다. 계속하시겠습니까?')) return;
    delDataBtn.disabled = true; delDataBtn.textContent = '삭제 중...';
    try {
      const json = await apiDeleteData();
      if (json.success) toast('삭제됨', 'success'); else toast('실패', 'error');
    } finally { delDataBtn.disabled = false; delDataBtn.textContent = '내 데이터 삭제 (방 + 메시지)'; }
  });

  const delAccountBtn = document.getElementById('btn-delete-account') as HTMLButtonElement;
  delAccountBtn?.addEventListener('click', async () => {
    if (!confirm('계정과 모든 데이터가 영구 삭제됩니다. 정말 탈퇴하시겠습니까?')) return;
    if (!confirm('정말로 탈퇴하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    delAccountBtn.disabled = true; delAccountBtn.textContent = '처리 중...';
    try {
      const json = await apiDeleteAccount();
      if (json.success) { localStorage.removeItem('kalpha_token'); localStorage.removeItem('kalpha_user'); localStorage.clear(); _app.navigateTo('login'); }
      else { toast(json.error || '실패', 'error'); delAccountBtn.disabled = false; delAccountBtn.textContent = '회원 탈퇴'; }
    } catch { delAccountBtn.disabled = false; delAccountBtn.textContent = '회원 탈퇴'; }
  });
}

function bindLabs() {
  bindSel('set-font-size', 'fontSize', applyFontSize);
  bindColor('set-bubble-color', 'bubbleColor', applyBubbleColor);
  bindSel('set-bubble-shape', 'bubbleShape', applyBubbleShape);
  bindSel('set-time-format', 'timeFormat', null);
  bindChk('set-join-notify', 'joinNotify', null);
  bindChk('set-sidebar', 'sidebarMode', applySidebarMode);
  bindSel('set-anim-speed', 'animSpeed', applyAllSettings);
  bindChk('set-compact', 'compactMode', applyAllSettings);
  bindChk('set-contrast', 'highContrast', applyAllSettings);
  bindChk('set-classic', 'classicTheme', applyAllSettings);
  bindChk('set-rainbow', 'rainbowMode', applyAllSettings);
  bindChk('set-markdown', 'markdownMode', null);
  bindSel('set-input-lines', 'inputMaxLines', applyInputLines);
  bindSel('set-poll-interval', 'pollInterval', null);

  document.getElementById('btn-force-reset')?.addEventListener('click', () => {
    if (!confirm('로컬 캐시(암호화 키, 설정 등)가 모두 초기화됩니다. 계속하시겠습니까?')) return;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) { const k = localStorage.key(i); if (k) keys.push(k); }
    keys.forEach(k => localStorage.removeItem(k));
    toast('초기화 완료', 'success');
  });
}

function bindSel(id: string, key: string, cb: (() => void) | null) {
  const el = document.getElementById(id) as HTMLSelectElement; if (!el) return;
  el.addEventListener('change', () => { ss(key, el.value); if (cb) cb(); });
}
function bindChk(id: string, key: string, cb: (() => void) | null) {
  const el = document.getElementById(id) as HTMLInputElement; if (!el) return;
  el.addEventListener('change', () => { ss(key, el.checked); if (cb) cb(); });
}
function bindColor(id: string, key: string, cb: (() => void) | null) {
  const el = document.getElementById(id) as HTMLInputElement; if (!el) return;
  el.addEventListener('input', () => { ss(key, el.value); if (cb) cb(); });
}

function applyAllSettings() { applyFontSize(); applyBubbleColor(); applyBubbleShape(); applyAnimSpeed(); applyCompactMode(); applyHighContrast(); applyClassicTheme(); applyRainbowMode(); applySidebarMode(); }
function applyFontSize() { const s: Record<string,string> = {small:'14px',medium:'16px',large:'18px'}; document.documentElement.style.fontSize = s[gs('fontSize','medium')]||'16px'; }
function applyBubbleColor() { document.documentElement.style.setProperty('--msg-mine-color', gs('bubbleColor','#2d6a4f')); }
function applyAnimSpeed() { const d: Record<string,string> = {'0.5x':'0.4s','1x':'0.2s','2x':'0.1s',off:'0s'}; document.documentElement.style.setProperty('--anim-duration', d[gs('animSpeed','1x')]||'0.2s'); }
function applyCompactMode() { document.documentElement.classList.toggle('compact-mode', gs('compactMode',false)); }
function applyHighContrast() { document.documentElement.classList.toggle('high-contrast', gs('highContrast',false)); }
function applyClassicTheme() { document.documentElement.classList.toggle('classic-theme', gs('classicTheme',false)); }
function applyRainbowMode() { document.documentElement.classList.toggle('rainbow-mode', gs('rainbowMode',false)); }
function applyBubbleShape() {
  const s = gs('bubbleShape','round');
  document.documentElement.classList.remove('bubble-shape-square','bubble-shape-tail');
  if (s === 'square') document.documentElement.classList.add('bubble-shape-square');
  if (s === 'tail') document.documentElement.classList.add('bubble-shape-tail');
}
function applySidebarMode() { document.documentElement.classList.toggle('sidebar-mode', gs('sidebarMode',false)); }
function applyInputLines() { const v = gs('inputMaxLines','unlimited'); document.documentElement.style.setProperty('--input-max-h', (v==='unlimited'?96:parseInt(v)*24)+'px'); }
